import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import OpportunityChart from './OpportunityChart';
import apiService from '../services/apiService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { decodeId } from '../utils/urlSecurity';

// Using OpportunityChart component instead of basic Chart.js

const UserResults = () => {
  const { submissionId: encodedSubmissionId } = useParams();
  const [resultsData, setResultsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isDownloadingChart, setIsDownloadingChart] = useState(false);
  
  // Decode the submission ID from the URL parameter
  const submissionId = decodeId(encodedSubmissionId);

  // Strategic category definitions and colors based on median calculation
  const categoryInfo = {
    'Quick Wins': {
      description: 'tech_score > tech_median AND roi_score > roi_median - prioritize for immediate implementation',
      color: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.6)'
    },
    'Strategic Challenges': {
      description: 'tech_score <= tech_median AND roi_score > roi_median - valuable but technically challenging',
      color: '#007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.6)'
    },
    'Technical Experiments': {
      description: 'tech_score > tech_median AND roi_score <= roi_median - pilot programs recommended',
      color: '#ffc107',
      backgroundColor: 'rgba(255, 193, 7, 0.6)'
    },
    'Lower Priority': {
      description: 'tech_score <= tech_median AND roi_score <= roi_median - consider for future phases',
      color: '#6c757d',
      backgroundColor: 'rgba(108, 117, 125, 0.6)'
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchResultsData = async () => {
      if (!submissionId) {
        setError('No submission ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const data = await apiService.getResults(encodedSubmissionId);
        setResultsData(data);
        
      } catch (err) {
        console.error('Error fetching results data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResultsData();
  }, [encodedSubmissionId, submissionId]);

  const downloadChart = async () => {
    if (!resultsData) return;
    
    setIsDownloadingChart(true);
    
    try {
      // Get the elements we need to capture
      const chartElement = document.getElementById('user-assessment-chart');
      if (!chartElement) {
        throw new Error('Chart element not found');
      }

      // 1. First capture just the chart with its rendered canvas
      const chartCanvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      
      // 2. Get the assessment cards section
      const cardElements = document.querySelectorAll('.card');
      const assessmentCards = Array.from(cardElements).find(card => 
        card.querySelector('h3') && 
        card.querySelector('h3').textContent.includes('Tech Feasibility')
      )?.parentElement;
      
      let cardsCanvas = null;
      if (assessmentCards) {
        cardsCanvas = await html2canvas(assessmentCards, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });
      }
      
      // 3. Now create a combined canvas with both elements
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      
      // Set dimensions for the final canvas
      const padding = 40; // Padding around elements
      const headerHeight = 80; // Height for the title header
      const spacing = 30; // Space between chart and cards
      
      // Calculate final dimensions
      const width = Math.max(chartCanvas.width, cardsCanvas ? cardsCanvas.width : 0) + (padding * 2);
      const height = headerHeight + chartCanvas.height + (cardsCanvas ? spacing + cardsCanvas.height : 0) + (padding * 2);
      
      finalCanvas.width = width;
      finalCanvas.height = height;
      
      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Draw header with opportunity name
      ctx.fillStyle = '#313b4c';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('AI Opportunity Assessment', width / 2, padding + 30);
      
      ctx.font = 'normal 20px Arial';
      ctx.fillText(resultsData.opportunity_name || 'Assessment', width / 2, padding + 60);
      
      // Draw blue header with scores
      const headerBoxHeight = 80;
      const headerBoxY = padding + headerHeight;
      ctx.fillStyle = '#2c3e67';
      ctx.fillRect(padding, headerBoxY, width - (padding * 2), headerBoxHeight);
      
   
      
      // Draw scores
      ctx.font = 'normal 12px Arial';
      ctx.textAlign = 'left';
      const scoreX = padding + 20;
      ctx.fillText(`• Score: ROI Potential [ ${resultsData.roi_potential_score || 0} to 10 ]`, scoreX, headerBoxY + 65);
      ctx.fillText(`• Score: Tech Feasibility [ ${resultsData.tech_feasibility_score || 0} to 10 ]`, width / 2, headerBoxY + 65);
      ctx.fillText(`• Bubble Size: Strategic Value [ ${resultsData.strategic_value_score || 0} to 10 ]`, width - scoreX - 180, headerBoxY + 65);
      
      // Draw the chart
      ctx.drawImage(chartCanvas, padding, padding + headerHeight + headerBoxHeight + 10);
      
      // Draw the cards if available
      if (cardsCanvas) {
        ctx.drawImage(
          cardsCanvas, 
          padding, 
          padding + headerHeight + headerBoxHeight + chartCanvas.height + spacing
        );
      }
      
      // Convert canvas to data URL
      const imgData = finalCanvas.toDataURL('image/jpeg', 0.95);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = imgData;
      link.download = `AI_Assessment_Chart_${resultsData.opportunity_name?.replace(/\s+/g, '_') || 'Chart'}.jpg`;
      link.click();
      
    } catch (error) {
      console.error('Error downloading chart:', error);
      alert('Error downloading chart. Please try again.');
    } finally {
      setIsDownloadingChart(false);
    }
  };

  const generatePDF = async () => {
    if (!resultsData) return;
    
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text('AI Opportunity Assessment Report', pageWidth / 2, 20, { align: 'center' });
      
      // Opportunity details
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Opportunity: ${resultsData.opportunity_name || 'N/A'}`, 20, 40);
      pdf.text(`Assessment Date: ${new Date(resultsData.submitted_at).toLocaleDateString() || new Date().toLocaleDateString()}`, 20, 50);
      pdf.text(`Submission ID: ${encodedSubmissionId}`, 20, 60);
      
      // Scores section
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text('Assessment Scores', 20, 80);
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Technical Feasibility: ${resultsData.tech_feasibility_score}/10`, 30, 95);
      pdf.text(`ROI Potential: ${resultsData.roi_potential_score}/10`, 30, 105);
      pdf.text(`Strategic Value: ${resultsData.strategic_value_score}/10`, 30, 115);
      pdf.text(`Strategic Category: ${resultsData.strategic_category}`, 30, 125);
      
      // Category description
      const categoryDesc = categoryInfo[resultsData.strategic_category]?.description || '';
      pdf.text('Category Description:', 20, 140);
      pdf.text(categoryDesc, 20, 150, { maxWidth: pageWidth - 40 });
      
      // Capture chart
      const chartElement = document.getElementById('user-assessment-chart');
      if (chartElement) {
        const canvas = await html2canvas(chartElement, {
          backgroundColor: '#ffffff',
          scale: 2
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 40;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Add new page if needed
        if (imgHeight > 100) {
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 20, 20, imgWidth, Math.min(imgHeight, 250));
        } else {
          pdf.addImage(imgData, 'PNG', 20, 170, imgWidth, imgHeight);
        }
      }
      
      // Save PDF
      pdf.save(`AI_Assessment_${resultsData.opportunity_name?.replace(/\s+/g, '_') || 'Report'}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Loading Your Results...</h2>
          <p>Please wait while we load your assessment results.</p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f3f3', 
              borderTop: '4px solid #0066cc', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <h2>Error Loading Results</h2>
          <p style={{ color: '#dc3545', marginBottom: '20px' }}>{error}</p>
          <div>
            <Link to="/" className="btn btn-primary" style={{ marginRight: '10px' }}>
              Take New Assessment
            </Link>
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!resultsData) {
    return (
      <div className="container">
        <div className="card">
          <h2>No Data Available</h2>
          <p>No assessment data found. Please take a new assessment.</p>
          <Link to="/" className="btn btn-primary">
            Start Assessment
          </Link>
        </div>
      </div>
    );
  }

  // No need for manual chart data preparation - OpportunityChart handles this

  return (
    <div className="container">
      <div className="header">
        <h1>Your Assessment Results</h1>
        <p>AI Opportunity Analysis Report</p>
      </div>

      {/* Opportunity Summary */}
      <div className="card">
        <h2>Opportunity Summary</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div>
            <h3>{resultsData.opportunity_name || 'N/A'}</h3>
            <p><strong>Description:</strong> {resultsData.opportunity_description || 'N/A'}</p>
            <p><strong>Submission ID:</strong> {encodedSubmissionId}</p>
            {resultsData.submitted_at && (
              <p><strong>Assessed on:</strong> {new Date(resultsData.submitted_at).toLocaleDateString()}</p>
            )}
          </div>
          <div>
            <h3>Assessment Scores</h3>
            <p><strong>Technical Feasibility:</strong> {resultsData.tech_feasibility_score}/10</p>
            <p><strong>ROI Potential:</strong> {resultsData.roi_potential_score}/10</p>
            <p><strong>Strategic Value:</strong> {resultsData.strategic_value_score}/10</p>
            <p><strong>Strategic Category:</strong> 
              <span 
                style={{ 
                  color: categoryInfo[resultsData.strategic_category]?.color || '#333',
                  fontWeight: 'bold',
                  marginLeft: '5px'
                }}
              >
                {resultsData.strategic_category}
              </span>
            </p>

          </div>
        </div>
      </div>

     

      {/* Chart Visualization */}
      <div className="card">
        <h2>Assessment Matrix</h2>
        <div 
          id="user-assessment-chart"
          style={{ 
            minHeight: '700px', 
            position: 'relative',
            backgroundColor: '#ffffff',
            padding: '20px',
            border: '1px solid #e1e5e9',
            borderRadius: '8px',
            marginBottom: '20px',
            overflow: 'visible'
          }}
        >
          {submissionId && resultsData && (
            <OpportunityChart 
              key={`user-chart-${submissionId}`}
              submissionId={parseInt(submissionId)} 
              isPortfolio={false}
              isPublic={true}
              preloadedData={resultsData.chart_data}
              isResultsView={true}
            />
          )}
        </div>
      </div>
            {/* Detailed Assessment Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        
        {/* Tech Feasibility Assessment Card */}
        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '2px solid #28a745' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#28a745', margin: 0 }}>Tech Feasibility Assessment</h3>
            <span style={{ 
              backgroundColor: '#28a745', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}>
              +{resultsData.tech_feasibility_score}
            </span>
          </div>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>Predictable patterns in process flow (~5)</li>
            <li>Cloud services acceptable for implementation (~4)</li>
            <li>APIs available for required systems (+6)</li>
            <li>Well-documented requirements</li>
          </ul>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: '#d4edda', 
            borderRadius: '6px',
            borderLeft: '4px solid #28a745'
          }}>
            <strong>Rationale:</strong> High automation potential with structured templates and established data sources.
          </div>
        </div>

        {/* ROI Potential Assessment Card */}
        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '2px solid #ffc107' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#856404', margin: 0 }}>ROI Potential</h3>
            <span style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}>
              +{resultsData.roi_potential_score}
            </span>
          </div>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>High daily transaction volume</li>
            <li>25% time savings expected</li>
            <li>Moderate change management needed</li>
            <li>Significant risk reduction potential (~9)</li>
          </ul>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '6px',
            borderLeft: '4px solid #ffc107'
          }}>
            <strong>Rationale:</strong> Significant cost savings from automation and substantial risk reduction.
          </div>
        </div>

        {/* Strategic Value Assessment Card */}
        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '2px solid #007bff' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#007bff', margin: 0 }}>Strategic Value</h3>
            <span style={{ 
              backgroundColor: '#007bff', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}>
              {resultsData.strategic_value_score}
            </span>
          </div>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>Direct alignment with regulatory directives</li>
            <li>Major brand protection from compliance</li>
            <li>Core operational risk management</li>
          </ul>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: '#d1ecf1', 
            borderRadius: '6px',
            borderLeft: '4px solid #007bff'
          }}>
            <strong>Rationale:</strong> Mission-critical for regulatory standing and operational excellence.
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            style={{
              backgroundColor: '#6366f1',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '8px',
              cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
              minWidth: '200px',
              minHeight: '60px',
              marginRight: '15px',
              opacity: isGeneratingPDF ? 0.6 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              if (!isGeneratingPDF) {
                e.target.style.backgroundColor = '#5b21b6';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseOut={(e) => {
              if (!isGeneratingPDF) {
                e.target.style.backgroundColor = '#6366f1';
                e.target.style.transform = 'translateY(0px)';
                e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF Report'}
          </button>
          
          <button 
            onClick={downloadChart}
            disabled={isDownloadingChart}
            style={{
              backgroundColor: '#22c55e',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '8px',
              cursor: isDownloadingChart ? 'not-allowed' : 'pointer',
              minWidth: '200px',
              minHeight: '60px',
              marginRight: '15px',
              opacity: isDownloadingChart ? 0.6 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              if (!isDownloadingChart) {
                e.target.style.backgroundColor = '#15803d';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseOut={(e) => {
              if (!isDownloadingChart) {
                e.target.style.backgroundColor = '#22c55e';
                e.target.style.transform = 'translateY(0px)';
                e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            {isDownloadingChart ? 'Downloading...' : 'Download Full Chart & Cards'}
          </button>

          <Link 
            to="/"
            style={{
              backgroundColor: 'white',
              color: '#6366f1',
              border: '2px solid #6366f1',
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '8px',
              textDecoration: 'none',
              minWidth: '200px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#6366f1';
              e.target.style.color = 'white';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#6366f1';
              e.target.style.transform = 'translateY(0px)';
              e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
          >
            Take New Assessment
          </Link>
        </div>
        
        <div style={{ marginTop: '20px', color: '#6b7280' }}>
          <p>Need help interpreting your results? Contact our AI assessment team for personalized guidance.</p>
        </div>
      </div>

    </div>
  );
};

export default UserResults;